import React from "react";
import { useReviewCTAVisibility } from "./useReviewCTAVisibility";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import googleIcon from "../../assets/icons/feedback-google.svg";
import yelpIcon from "../../assets/icons/feedback-yelp.svg";
import facebookIcon from "../../assets/icons/feedback-facebook.svg";
import type {
  FlowComponentRenderProps,
  ReviewSelectionComponentConfig,
} from "../types";
import HtmlText from "../components/HtmlText";
import { reviewSiteMeta } from "../components/selection/reviewSiteMeta";
import { useCompanyBrandColorScheme } from "../CompanyBrandProvider";

/** Figma defaults for the Direct Review prompt card (U+2019 apostrophe, unspaced U+2014 em dash). */
const RECOGNIZE_TITLE = "Want us to recognize your tech?";
const RECOGNIZE_BODY =
  "Leave a Google review, and we\u2019ll automatically match it to your service so they get credit\u2014no need to mention their name.";
const BONUS_BODY =
  "Leave a Google review, and we\u2019ll automatically match it to your service and send them the bonus\u2014no need to mention their name.";

/** Module / legacy titles that should yield to Figma prompt defaults. */
const PLACEHOLDER_TITLES = new Set([
  "",
  "Choose where to post",
  "Let us tip your tech",
  "Share your feedback publicly.",
]);

function isPlaceholderTitle(title: string) {
  if (PLACEHOLDER_TITLES.has(title)) return true;
  // Rate-config / admin defaults historically looked like "Let us tip your tech $5".
  return /^Let us tip your tech\b/i.test(title);
}

/** Rate-config schema/form defaults that should yield to Figma body copy. */
function isPlaceholderBonusMessage(message: string) {
  // Schema: "...{{TipAmount}}..." and form defaults with a literal $ amount.
  return /^Write a positive review about your tech\b/i.test(message);
}

function reviewButtonSx(
  config: ReviewSelectionComponentConfig,
  selected: boolean,
) {
  const filled = (config.buttonStyle ?? "filled") === "filled";
  const borderColor = selected
    ? "var(--cx-brand-primary-dark)"
    : filled
      ? "var(--cx-brand-button-selected-border)"
      : "var(--cx-brand-button-border)";

  return {
    bgcolor: filled
      ? selected
        ? "var(--cx-brand-primary-dark)"
        : "var(--cx-brand-button-selected-bg)"
      : "var(--cx-brand-button-bg)",
    border: "1px solid",
    borderColor,
    borderRadius: `${config.buttonBorderRadius ?? 8}px`,
    color: filled
      ? "var(--cx-brand-button-selected-text)"
      : "var(--cx-brand-button-text)",
    gap: 1,
    justifyContent: "center",
    textTransform: "none",
    minHeight: config.buttonMinHeight ?? 48,
    px: 2,
    py: 1,
    width: "100%",
    "&:hover": {
      bgcolor: filled
        ? "var(--cx-brand-primary-dark)"
        : "var(--cx-brand-button-hover-bg)",
      borderColor: filled
        ? "var(--cx-brand-primary-dark)"
        : "var(--cx-brand-primary)",
    },
  } as const;
}

function providerButtonStyle(config: ReviewSelectionComponentConfig, provider: string) {
  return config.buttonStyle ?? (provider === "google" ? "outlined" : "filled");
}

function providerButtonSx(
  config: ReviewSelectionComponentConfig,
  selected: boolean,
  provider: string
) {
  if (provider !== "google") return reviewButtonSx(config, selected);
  return {
    ...reviewButtonSx({ ...config, buttonStyle: providerButtonStyle(config, provider) }, selected),
    minHeight: config.buttonMinHeight ?? 64,
    gap: 0,
    textAlign: "start",
  } as const;
}

function compactProviderButtonSx(
  config: ReviewSelectionComponentConfig,
  selected: boolean,
) {
  return {
    bgcolor: selected
      ? "var(--cx-brand-primary-soft)"
      : "var(--cx-brand-surface)",
    border: "1px solid",
    borderColor: selected
      ? "var(--cx-brand-primary)"
      : "var(--cx-brand-button-border)",
    borderRadius: `${config.buttonBorderRadius ?? 8}px`,
    color: "var(--cx-brand-text)",
    gap: 1.25,
    justifyContent: "center",
    minHeight: config.buttonMinHeight ?? 60,
    px: 2,
    py: 1,
    textAlign: "center",
    textTransform: "none",
    width: "100%",
    "&:hover": {
      bgcolor: "var(--cx-brand-primary-soft)",
      borderColor: "var(--cx-brand-primary)",
    },
  } as const;
}

function googleReviewLabel(config: ReviewSelectionComponentConfig, providerLabel: string) {
  const template = config.reviewButtonLabel?.trim();
  // Migrate the contract's previous default while preserving authored/localized copy.
  return !template || template === "Write a Review on {provider}"
    ? "Leave a Google review"
    : reviewButtonText(config, providerLabel);
}

function googleAccessibleLabel(config: ReviewSelectionComponentConfig, providerLabel: string) {
  const label = googleReviewLabel(config, providerLabel);
  return /google/i.test(label) ? label : `${label} — Google`;
}

/** Google CTA follows the supplied reference; custom copy and commands stay owned by Studio. */
function ReviewButtonContent({
  compactSummary = false,
  config,
  site,
}: {
  compactSummary?: boolean;
  config: ReviewSelectionComponentConfig;
  site: ReviewSelectionComponentConfig["sites"][number];
}) {
  const provider = site.provider ?? site.id;
  if (compactSummary)
    return (
      <>
        <Box
          aria-hidden="true"
          sx={{
            alignItems: "center",
            display: "flex",
            flexShrink: 0,
            height: 30,
            justifyContent: "center",
            width: 30,
          }}
        >
          <ProviderIcon label={site.label} provider={provider} />
        </Box>
        <Typography
          component="span"
          sx={{
            color: "inherit",
            fontSize: 16,
            fontWeight: 700,
            lineHeight: "22px",
            overflowWrap: "anywhere",
          }}
        >
          {reviewButtonText(config, site.label)}
        </Typography>
      </>
    );
  if (provider === "google")
    return (
      <Box
        component="span"
        data-testid="google-review-button-layout"
        sx={{
          alignItems: "center",
          display: "grid",
          gridTemplateColumns: "20px minmax(0, 1fr) 20px",
          width: "100%",
        }}
      >
        <Box component="span" aria-hidden="true" sx={{ width: 20 }} />
        <Stack
          component="span"
          direction="row"
          spacing={1.5}
          sx={{ alignItems: "center", justifySelf: "center", maxWidth: "100%", minWidth: 0 }}
        >
          <Box
            component="img"
            src={googleIcon}
            alt=""
            aria-hidden="true"
            sx={{ width: 32, height: 32, flexShrink: 0 }}
          />
          <Stack spacing={0.25} sx={{ minWidth: 0, alignItems: "flex-start" }}>
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                color: "#F5B400",
                fontSize: 14,
                lineHeight: "16px",
                letterSpacing: "2px",
                whiteSpace: "nowrap",
              }}
            >
              ★★★★★
            </Box>
            <Typography
              component="span"
              sx={{
                color: "inherit",
                fontSize: 14,
                fontWeight: 600,
                lineHeight: "20px",
                overflowWrap: "anywhere",
              }}
            >
              {googleReviewLabel(config, site.label)}
            </Typography>
          </Stack>
        </Stack>
        <ChevronRightRoundedIcon
          aria-hidden="true"
          sx={{ width: 20, height: 20, justifySelf: "end", opacity: 0.6 }}
        />
      </Box>
    );
  return (
    <>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexShrink: 0,
          height: 24,
          justifyContent: "center",
          width: 24,
        }}
      >
        <ProviderIcon label={site.label} provider={provider} />
      </Box>
      <Typography
        sx={{
          color: "inherit",
          fontSize: 15,
          fontWeight:
            config.variant === "no_nps_v3" || config.showFeedbackPreview !== false ? 700 : 600,
          lineHeight: "26px",
        }}
      >
        {reviewButtonText(config, site.label)}
      </Typography>
    </>
  );
}

function bonusDefaultTitle(amount: number) {
  return `Want us to send your tech a $${amount} bonus?`;
}

function ProviderIcon({
  label,
  provider,
}: {
  label: string;
  provider: string;
}) {
  if (provider === "google") {
    return (
      <Box
        alt="Google"
        component="img"
        src={googleIcon}
        sx={{ height: 24, width: 24 }}
      />
    );
  }

  if (provider === "facebook") {
    return (
      <Box
        alt="Facebook"
        component="img"
        src={facebookIcon}
        sx={{ height: 24, width: 24 }}
      />
    );
  }

  if (provider === "yelp") {
    return <Box component="img" alt="Yelp" src={yelpIcon} sx={{ width: 19.0046, height: 24 }} />;
  }

  const meta = reviewSiteMeta(provider, label);
  return (
    <Box
      component="span"
      sx={{
        color: meta.color,
        fontSize: provider === "bbb" ? 11 : 22,
        fontWeight: 900,
      }}
    >
      {meta.mark}
    </Box>
  );
}

/**
 * Direct-review prompt card — Figma nodes 2583:75575 / 2583:75990.
 * Screen chrome (header/subheading) stays left-aligned; this card is center-aligned.
 */
function ReviewPromptCard({
  body,
  compactSummary = false,
  config,
  onSelectSite,
  selectedSiteId,
  sites,
  title,
}: {
  body: string;
  compactSummary?: boolean;
  config: ReviewSelectionComponentConfig;
  onSelectSite: (site: ReviewSelectionComponentConfig["sites"][number]) => void;
  selectedSiteId?: string;
  sites: ReviewSelectionComponentConfig["sites"];
  title: string;
}) {
  const dark = useCompanyBrandColorScheme() === "dark";
  return (
    <Box
      sx={{
        bgcolor: compactSummary || dark ? "transparent" : "#F4F6F8",
        border:
          compactSummary || dark ? 0 : "1px solid rgba(145, 158, 171, 0.20)",
        borderRadius: compactSummary ? 0 : 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        overflow: "hidden",
        pb: compactSummary || dark ? 0 : 1.25,
        pt: compactSummary || dark ? 0 : 1,
        px: compactSummary || dark ? 0 : 1.25,
        width: "100%",
      }}
    >
      {!compactSummary && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            mx: "auto",
            maxWidth: 310,
            textAlign: "center",
            width: "100%",
          }}
        >
          <Typography
            sx={{
              color: "var(--cx-brand-text)",
              fontSize: 16,
              fontWeight: 700,
              lineHeight: "24px",
              textAlign: "center",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              color: "var(--cx-brand-text-muted)",
              fontSize: 13,
              lineHeight: "20px",
              textAlign: "center",
            }}
          >
            {body}
          </Typography>
        </Box>
      )}

      <Stack spacing={0.75} sx={{ width: "100%" }}>
        {sites.map((site) => {
          const selected = selectedSiteId === site.id;
          const providerId = site.provider ?? site.id;

          return (
            <Button
              color="inherit"
              key={site.id}
              data-review-provider={site.provider}
              onClick={() => onSelectSite(site)}
              sx={
                compactSummary
                  ? compactProviderButtonSx(config, selected)
                  : providerButtonSx(config, selected, providerId)
              }
              aria-label={
                !compactSummary && providerId === "google"
                  ? googleAccessibleLabel(config, site.label)
                  : undefined
              }
              type="button"
              variant={
                compactSummary ||
                providerButtonStyle(config, providerId) === "outlined"
                  ? "outlined"
                  : "contained"
              }
            >
              <ReviewButtonContent
                compactSummary={compactSummary}
                config={config}
                site={site}
              />
            </Button>
          );
        })}
      </Stack>
    </Box>
  );
}

function hasGoogleDestination(config: ReviewSelectionComponentConfig) {
  return config.sites.some((site) => (site.provider ?? site.id) === "google");
}

function resolvePromptTitle(
  config: ReviewSelectionComponentConfig,
  isBonus: boolean,
  bonusAmount: number,
) {
  const raw = (config.title || "").trim();
  // Stored generated bonus titles may retain an earlier amount after an edit.
  const shippedGoogleTitle = raw === RECOGNIZE_TITLE || /^Want us to send your tech a \$\d+(?:\.\d+)? bonus\?$/.test(raw);
  if (raw && !isPlaceholderTitle(raw) && (hasGoogleDestination(config) || !shippedGoogleTitle)) {
    return raw;
  }
  if (!hasGoogleDestination(config)) return "Share your experience";
  return isBonus ? bonusDefaultTitle(bonusAmount) : RECOGNIZE_TITLE;
}

function resolvePromptBody(
  config: ReviewSelectionComponentConfig,
  isBonus: boolean,
) {
  const configuredBody = (config.reviewPromptBody || "").trim();
  const shippedGoogleBody = configuredBody === RECOGNIZE_BODY || configuredBody === BONUS_BODY;
  if (configuredBody && (hasGoogleDestination(config) || !shippedGoogleBody)) {
    return configuredBody;
  }
  const raw = (config.bonusMessage || "").trim();
  if (raw && !isPlaceholderBonusMessage(raw)) {
    return raw;
  }
  if (!hasGoogleDestination(config)) return "Share your experience with a review.";
  return isBonus ? BONUS_BODY : RECOGNIZE_BODY;
}

function reviewButtonText(
  config: ReviewSelectionComponentConfig,
  label: string,
) {
  const template = (config.reviewButtonLabel || "").trim();
  return template
    ? template.replace(/\{provider\}/gi, label)
    : `Write a Review on ${label}`;
}

const FeedbackReviewSelection: React.FC<
  FlowComponentRenderProps<ReviewSelectionComponentConfig> & {
    onReviewCTAVisible?: (provider: string) => Promise<void>;
    compactSummary?: boolean;
    pending?: boolean;
    ownedActions?: Array<{ id: string; type: string }>;
  }
> = ({
  compactSummary = false,
  config,
  onInteract,
  value,
  pending = false,
  ownedActions = [],
  onReviewCTAVisible,
}) => {
  const visibilityRef = useReviewCTAVisibility(JSON.stringify(config.sites.map(site => [site.id, site.provider, site.url])), pending, onReviewCTAVisible);
  const ownedMetadata = (type: string) => {
    const actions = ownedActions.filter((action) => action.type === type);
    return actions.length === 1 ? { actionId: actions[0].id } : {};
  };
  const selectedSiteId = typeof value === "string" ? value : undefined;
  const isBonusPrompt = config.variant === "no_nps_v3";
  const isDirectReviewPrompt =
    config.sites.length > 0 && (isBonusPrompt || config.showFeedbackPreview !== false);
  const bonusAmount = config.bonusAmount ?? 5;

  const selectSite = (
    site: ReviewSelectionComponentConfig["sites"][number],
  ) => {
    if (pending) return;
    onInteract?.({
      componentId: config.id,
      label: site.label,
      metadata: {
        ...ownedMetadata("review"),
        destinationSource: site.destinationSource,
        locationLabel: site.locationLabel,
        provider: site.provider,
        strategy: site.strategy,
        weight: site.weight,
      },
      type: "review",
      url: site.url,
      value: site.id,
    });
  };

  return (
    <Card
      ref={visibilityRef}
      sx={{
        bgcolor: "transparent",
        border: 0,
        borderRadius: 0,
        boxShadow: "none",
        color: "var(--cx-brand-text)",
        p: 0,
      }}
    >
      <Stack component="fieldset" disabled={pending} aria-busy={pending || undefined} spacing={1.25} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
        {isDirectReviewPrompt ? (
          <ReviewPromptCard
            body={resolvePromptBody(config, isBonusPrompt)}
            compactSummary={compactSummary}
            config={config}
            onSelectSite={selectSite}
            selectedSiteId={selectedSiteId}
            sites={config.sites}
            title={resolvePromptTitle(config, isBonusPrompt, bonusAmount)}
          />
        ) : (
          <Stack spacing={1}>
            {config.title ? (
              <HtmlText
                component="div"
                html={config.title}
                variant="subtitle2"
                data-feedback-review-title
                sx={{ textAlign: "center", fontSize: 16, fontWeight: 600, lineHeight: "24px" }}
              />
            ) : null}

            <Stack spacing={0.75}>
              {config.sites.map((site) => {
                const selected = selectedSiteId === site.id;
                const providerId = site.provider ?? site.id;

                return (
                  <Button
                    color="inherit"
                    key={site.id}
              data-review-provider={site.provider}
                    onClick={() => selectSite(site)}
                    sx={providerButtonSx(config, selected, providerId)}
                    aria-label={providerId === "google" ? googleAccessibleLabel(config, site.label) : undefined}
                    type="button"
                    variant={
                      providerButtonStyle(config, providerId) === "outlined"
                        ? "outlined"
                        : "contained"
                    }
                  >
                    <ReviewButtonContent config={config} site={site} />
                  </Button>
                );
              })}
            </Stack>
          </Stack>
        )}

        {config.callbackPhone && config.callbackLabel && (ownedActions.length === 0 || ownedActions.some((action) => action.type === 'callback')) ? (
          <ButtonBase
            onClick={() =>
              onInteract?.({
                componentId: config.id,
                label: config.callbackLabel,
                phone: config.callbackPhone,
                metadata: ownedMetadata("callback"),
                type: "callback",
                value: true,
              })
            }
            sx={{
              color: "var(--cx-brand-text-muted)",
              fontSize: 14,
              lineHeight: "22px",
              minHeight: 44,
              px: 1,
              mx: "auto",
            }}
            type="button"
          >
            Prefer to talk to someone?&nbsp;
            <Box
              component="span"
              sx={{ color: "var(--cx-brand-text)", fontWeight: 600 }}
            >
              {config.callbackLabel}
            </Box>
          </ButtonBase>
        ) : null}

        {config.tipTargetScreenId ? (
          <ButtonBase
            onClick={() => {
              if (!pending) onInteract?.({ componentId: config.id, label: "Leave a tip", metadata: { ...ownedMetadata("tip"), technicianName: config.technicianName }, type: "tip", value: true });
            }}
            sx={{
              alignItems: "center",
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: 0.75,
              display: "flex",
              minHeight: 60,
              mt: "16px !important",
              px: 1.5,
              py: 1,
              textAlign: "left",
              width: "100%",
              "&:hover": { bgcolor: "grey.200" },
            }}
            type="button"
          >
            <Box
              aria-hidden="true"
              sx={{
                alignItems: "center",
                background:
                  "radial-gradient(circle at 35% 30%, #FFE16A 0 18%, #F3BB16 19% 63%, #D99A00 64% 100%)",
                border: "2px solid #FFC928",
                borderRadius: "50%",
                boxShadow: "inset 0 0 0 3px rgba(255,255,255,0.28)",
                color: "#A66B00",
                display: "flex",
                flexShrink: 0,
                fontSize: 25,
                fontWeight: 900,
                height: 44,
                justifyContent: "center",
                mr: 1.25,
                width: 44,
              }}
            >
              $
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{ fontSize: 14, fontWeight: 600, lineHeight: "22px" }}
              >
                Enjoyed the service?
              </Typography>
              <Typography
                sx={{
                  color: "var(--cx-brand-text-muted)",
                  fontSize: 14,
                  lineHeight: "22px",
                }}
              >
                Leave {config.technicianName ?? "your technician"} a tip
              </Typography>
            </Box>
            <ChevronRightRoundedIcon sx={{ fontSize: 26 }} />
          </ButtonBase>
        ) : null}
      </Stack>
    </Card>
  );
};

export default FeedbackReviewSelection;
